import { AlertCircle, Info, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatPrice } from "../../../lib/formatPrice";
import LabBookingLayout from "../components/LabBookingLayout";
import { useLabBooking } from "../context/LabBookingContext";

const CartPage = () => {
  const navigate = useNavigate();
  const { selectedTests, user, selectedLab, collectionType } = useLabBooking();

  const lineItems = selectedLab?.testList || [];
  const subtotal  = lineItems.reduce((sum, i) => sum + (Number(i.price) - (Number(i.price) * Number(i.discount || 0)) / 100), 0);
  const mrpTotal  = lineItems.reduce((sum, i) => sum + Number(i.price), 0);
  const savings   = mrpTotal - subtotal;
  const isHome    = collectionType !== "lab";

  const handleCheckout = () => {
    if (!user) { navigate("/lab-booking/login"); return; }
    // User logged in but abandoned the profile step — send back to finish it.
    if (!user.first_name) { navigate("/lab-booking/login"); return; }
    navigate("/lab-booking/patient-details");
  };

  return (
    <LabBookingLayout>
      <h1 className="text-[22px] font-extrabold text-[#191C1D] mb-4">Cart</h1>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {selectedTests.map((test) => (
          <span
            key={test.is_package ? `package:${test.name}` : test.is_unmapped ? `unmapped:${test.name}` : `test:${test.test_master_id}`}
            className="text-[12px] px-3 py-1.5 rounded-full border border-gray-200 bg-white text-[#3D4945]"
          >
            {test.name}
          </span>
        ))}
        <button
          onClick={() => navigate("/?section=lab-booking")}
          className="flex items-center gap-1 text-[12px] px-3 py-1.5 rounded-full border border-dashed border-[#13B58E] text-[#13B58E]"
        >
          <Plus size={12} /> Add
        </button>
      </div>

      {!selectedLab && (
        <p className="text-[13px] text-[#6B7A99] mb-4">
          Your cart is empty — pick a lab from Compare Labs first.
        </p>
      )}

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 space-y-3">
          {selectedLab?.lab && (
            <div className="flex items-center justify-between mb-1">
              <p className="text-[13px] font-semibold text-[#3D4945]">{selectedLab.lab.name}</p>
              <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#EEF3F2] text-[#13B58E]">
                {isHome ? "Home Collection" : "Lab Visit"}
              </span>
            </div>
          )}

          {lineItems.map((item) => {
            const finalPrice = Number(item.price) - (Number(item.price) * Number(item.discount || 0)) / 100;
            return (
              <div key={item.id} className="bg-white rounded-2xl border border-gray-100 p-5">
                <p className="text-[15px] font-bold text-[#191C1D]">{item.name}</p>
                <p className="text-[12px] text-[#6B7A99] mt-0.5">
                  {item.test_count ? `${item.test_count} Tests Included` : item.is_package ? "Package" : "Test"}
                </p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[16px] font-extrabold text-[#191C1D]">₹{formatPrice(finalPrice)}</span>
                    {Number(item.discount) > 0 && (
                      <span className="text-[12px] text-[#9AA6A4] line-through">₹{formatPrice(item.price)}</span>
                    )}
                  </div>
                  {Number(item.discount) > 0 && (
                    <span className="text-[11px] font-semibold text-[#13B58E] bg-[#EEF3F2] px-2 py-0.5 rounded-full">
                      {Math.round(Number(item.discount))}% off
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {isHome && (
            <div className="flex items-start gap-2 bg-[#EEF9F4] border border-[#B9E6D3] rounded-xl p-3.5 text-[12px] text-[#1F7A5C]">
              <Info size={15} className="shrink-0 mt-0.5" />
              <span>
                A <strong>home collection fee</strong> (₹150–₹200 depending on the lab) will be added in the next step. Your final payable amount will be confirmed on the Review Order screen.
              </span>
            </div>
          )}

          {!user && (
            <div className="flex items-start gap-2 bg-[#FFF8E6] border border-[#F5D88C] rounded-xl p-3.5 text-[12px] text-[#8A6D1F]">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              Sign in is required to place an order. You'll be prompted to sign in when you click Proceed to Checkout.
            </div>
          )}
        </div>

        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 sticky top-24">
            <h3 className="text-[14px] font-bold text-[#191C1D] mb-3">Order Summary</h3>
            <div className="space-y-2 text-[13px]">
              {lineItems.map((item) => {
                const mrp        = Number(item.price);
                const finalPrice = mrp - (mrp * Number(item.discount || 0)) / 100;
                const hasDiscount = Number(item.discount) > 0;
                return (
                  <div key={item.id}>
                    <span className="text-[#3D4945]">{item.name}</span>
                    <div className="flex items-baseline gap-1.5 mt-0.5">
                      {hasDiscount && (
                        <span className="text-[11px] text-[#9AA6A4] line-through">₹{formatPrice(mrp)}</span>
                      )}
                      <span className="text-[#191C1D] font-semibold">₹{formatPrice(finalPrice)}</span>
                      {hasDiscount && (
                        <span className="text-[10px] text-[#13B58E] font-semibold">
                          ({Math.round(Number(item.discount))}% off)
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {isHome && (
                <div className="flex justify-between text-[12px] text-[#9AA6A4] pt-1">
                  <span>Home Collection Fee</span>
                  <span>Added next step</span>
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 mt-3 pt-3">
              <div className="flex justify-between items-end">
                <span className="text-[14px] font-bold text-[#191C1D]">
                  {isHome ? "Test Subtotal" : "Total Amount"}
                </span>
                <div className="text-right">
                  {mrpTotal > subtotal && (
                    <p className="text-[11px] text-[#9AA6A4] line-through">₹{formatPrice(mrpTotal)}</p>
                  )}
                  <span className="text-[16px] font-extrabold text-[#191C1D]">₹{formatPrice(subtotal)}</span>
                </div>
              </div>
              {isHome && (
                <p className="text-[11px] text-[#9AA6A4] mt-1">
                  Excl. home collection fee — final total on next screen
                </p>
              )}
            </div>

            {savings > 0 && (
              <div className="bg-[#EEF3F2] text-[#13B58E] text-[12px] font-semibold rounded-lg px-3 py-2 mt-3 text-center">
                ★ HealthismPlus discount: ₹{formatPrice(savings)} off tests
              </div>
            )}

            <button
              onClick={handleCheckout}
              disabled={!selectedLab}
              className="w-full bg-[#13B58E] text-white text-[14px] font-semibold py-3 rounded-full mt-4 hover:bg-[#0fa07d] transition disabled:opacity-60"
            >
              Proceed to Checkout →
            </button>
            {!user && (
              <p className="text-[11px] text-[#9AA6A4] text-center mt-2">You'll be asked to sign in next</p>
            )}
          </div>
        </div>
      </div>
    </LabBookingLayout>
  );
};

export default CartPage;
